/**
 * api.js — PriceBuddy frontend API client
 * Wired to the real Laravel backend (routes/api.php)
 *
 * Public endpoints (no auth):
 *   GET /api/bantay-presyo/stats                             ← 404 → fallback to /stores
 *   GET /api/bantay-presyo/products/search?q=&country=
 *   GET /api/bantay-presyo/products/prices?product=&location=&country=
 *   GET /api/bantay-presyo/products/compare?product=&location=&country=
 *   GET /api/bantay-presyo/stores?location=&country=
 *   GET /api/bantay-presyo/stores/{store}/products?location=
 */

const BASE = (import.meta.env.VITE_API_URL ?? 'https://api-iponbuddy.vincedaniel.com/api').replace(/\/$/, '');

// ── Fetch wrapper ─────────────────────────────────────────────────────────────
async function get(path, params = {}, signal = null) {
  const endpoint = path.startsWith('/') ? path : `/${path}`;
  const url = new URL(`${BASE}${endpoint}`);
  Object.entries(params).forEach(([k, v]) => {
    if (v !== null && v !== undefined && v !== '') url.searchParams.set(k, String(v));
  });
  const opts = { headers: { Accept: 'application/json' }, mode: 'cors' };
  if (signal) opts.signal = signal;
  const res = await fetch(url.toString(), opts);
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`HTTP ${res.status}${text ? ': ' + text.slice(0, 120) : ''}`);
  }
  return res.json();
}

function capitalize(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

// ── Currency locale map ───────────────────────────────────────────────────────
const CURRENCY_LOCALE = {
  PHP: 'en-PH', SGD: 'en-SG', MYR: 'ms-MY', IDR: 'id-ID',
  THB: 'th-TH', VND: 'vi-VN', USD: 'en-US', AUD: 'en-AU',
  JPY: 'ja-JP', KRW: 'ko-KR', HKD: 'zh-HK', AED: 'ar-AE',
  GBP: 'en-GB', CAD: 'en-CA',
};

const NODECIMAL_CURRENCIES = new Set(['JPY', 'KRW', 'IDR', 'VND']);

// ── Formatters ────────────────────────────────────────────────────────────────
export function fmtPrice(value, currency = 'PHP') {
  if (value == null) return '—';
  const locale  = CURRENCY_LOCALE[currency] ?? 'en-US';
  const noDecim = NODECIMAL_CURRENCIES.has(currency);
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency', currency,
      minimumFractionDigits: noDecim ? 0 : 2,
      maximumFractionDigits: noDecim ? 0 : 2,
    }).format(value);
  } catch {
    return `${currency} ${Number(value).toFixed(noDecim ? 0 : 2)}`;
  }
}

export function fmtChange(absPercent) {
  return `${Number(absPercent).toFixed(1)}%`;
}

// ── International country + location data ─────────────────────────────────────
export const COUNTRIES = [
  { code: 'PH', name: 'Philippines',   flag: '🇵🇭', currency: 'PHP' },
  { code: 'SG', name: 'Singapore',     flag: '🇸🇬', currency: 'SGD' },
  { code: 'MY', name: 'Malaysia',      flag: '🇲🇾', currency: 'MYR' },
  { code: 'ID', name: 'Indonesia',     flag: '🇮🇩', currency: 'IDR' },
  { code: 'TH', name: 'Thailand',      flag: '🇹🇭', currency: 'THB' },
  { code: 'VN', name: 'Vietnam',       flag: '🇻🇳', currency: 'VND' },
  { code: 'US', name: 'United States', flag: '🇺🇸', currency: 'USD' },
  { code: 'AU', name: 'Australia',     flag: '🇦🇺', currency: 'AUD' },
  { code: 'JP', name: 'Japan',         flag: '🇯🇵', currency: 'JPY' },
  { code: 'KR', name: 'South Korea',   flag: '🇰🇷', currency: 'KRW' },
  { code: 'HK', name: 'Hong Kong',     flag: '🇭🇰', currency: 'HKD' },
  { code: 'AE', name: 'UAE',           flag: '🇦🇪', currency: 'AED' },
  { code: 'GB', name: 'UK',            flag: '🇬🇧', currency: 'GBP' },
  { code: 'CA', name: 'Canada',        flag: '🇨🇦', currency: 'CAD' },
];

export const LOCATIONS_BY_COUNTRY = {
  PH: [
    'Metro Manila','Quezon City','Makati','Pasig','Taguig',
    'Mandaluyong','Marikina','Parañaque','Las Piñas','Muntinlupa',
    'Caloocan','Malabon','Navotas','Valenzuela',
    'Cebu City','Davao City','Iloilo City','Cagayan de Oro',
    'Bacolod','Zamboanga City','Baguio City','Angeles City',
    'Antipolo','Biñan','San Jose del Monte',
  ],
  SG: ['Central','Orchard','Bugis','Jurong','Tampines','Woodlands','Ang Mo Kio','Bedok','Clementi','Toa Payoh'],
  MY: ['Kuala Lumpur','Petaling Jaya','Shah Alam','Subang Jaya','Johor Bahru','Penang','Kota Kinabalu','Kuching','Ipoh','Malacca'],
  ID: ['Jakarta','Surabaya','Bandung','Medan','Bekasi','Tangerang','Depok','Semarang','Palembang','Makassar'],
  TH: ['Bangkok','Chiang Mai','Phuket','Pattaya','Nonthaburi','Pak Kret','Hat Yai','Udon Thani','Khon Kaen','Chon Buri'],
  VN: ['Ho Chi Minh City','Hanoi','Da Nang','Hai Phong','Can Tho','Bien Hoa','Hue','Nha Trang','Buon Ma Thuot','Vung Tau'],
  US: ['New York','Los Angeles','Chicago','Houston','Phoenix','Philadelphia','San Antonio','San Diego','Dallas','San Jose','Seattle','Miami','Atlanta','Boston','Denver'],
  AU: ['Sydney','Melbourne','Brisbane','Perth','Adelaide','Gold Coast','Canberra','Hobart','Darwin','Geelong'],
  JP: ['Tokyo','Osaka','Yokohama','Nagoya','Sapporo','Fukuoka','Kobe','Kyoto','Kawasaki','Saitama'],
  KR: ['Seoul','Busan','Incheon','Daegu','Daejeon','Gwangju','Suwon','Ulsan','Changwon','Seongnam'],
  HK: ['Hong Kong Island','Kowloon','New Territories','Mong Kok','Wan Chai','Causeway Bay','Tsim Sha Tsui'],
  AE: ['Dubai','Abu Dhabi','Sharjah','Ajman','Ras Al Khaimah','Fujairah','Umm Al Quwain'],
  GB: ['London','Manchester','Birmingham','Leeds','Glasgow','Liverpool','Edinburgh','Bristol','Sheffield','Newcastle'],
  CA: ['Toronto','Montreal','Vancouver','Calgary','Edmonton','Ottawa','Winnipeg','Quebec City','Hamilton','Kitchener'],
};

export const PH_LOCATIONS = LOCATIONS_BY_COUNTRY.PH;

export const PH_STORES = [
  'SM Supermarket','Robinsons','Puregold','Savemore',
  'Landers','Landmark','Walter Mart','7-Eleven',"Rustan's",
  'Shopwise','Ministop','Lawson',
];

export function currencyFor(countryCode) {
  return COUNTRIES.find(c => c.code === countryCode)?.currency ?? 'PHP';
}

// ── Platform stats ────────────────────────────────────────────────────────────
// /stats 404s — fallback derives from /stores (always public).
// `reports` stays null → StatCard hides itself when null.
const _statsCache = { ts: 0, data: null };
const STATS_TTL_MS = 10 * 60 * 1000;

export async function getPlatformStats(country = 'PH') {
  const res = await get('/bantay-presyo/stats', { country });
  const d   = res?.data ?? {};
  return { products: d.products ?? null, reports: d.reports ?? null, cities: d.cities ?? null, stores: d.stores ?? null };
}

// ── Search ────────────────────────────────────────────────────────────────────
export async function searchPrices(query, { location = null, country = 'PH', signal = null } = {}) {
  const searchRes = await get('/bantay-presyo/products/search', { q: query, country }, signal);
  const products  = searchRes?.data ?? [];
  if (!products.length) return { data: [] };

  const enriched = await Promise.allSettled(
    products.slice(0, 6).map(async (p) => {
      try {
        const cmp  = await get('/bantay-presyo/products/compare', { product: p.product_name, location, country }, signal);
        const rows = cmp?.data ?? [];
        if (!rows.length) return { product_name: p.product_name, avg_price: p.min_price ?? null, store_name: null, location: location ?? null, scan_count: p.store_count ?? null };
        const cheapest = rows[0];
        return { product_name: cheapest.product_name, avg_price: cheapest.price, store_name: cheapest.store_name, location: cheapest.location, scan_count: rows.length };
      } catch (err) {
        if (err?.name === 'AbortError') throw err;
        return { product_name: p.product_name, avg_price: p.min_price ?? null, store_name: null, location: location ?? null, scan_count: p.store_count ?? null };
      }
    })
  );

  return {
    data: enriched
      .filter(r => r.status === 'fulfilled')
      .map(r => r.value)
      .filter(r => r.avg_price != null),
  };
}

// ── Price trend ───────────────────────────────────────────────────────────────
export async function getPriceTrend(productName, { location = null, country = 'PH', days = 30 } = {}) {
  const res    = await get('/bantay-presyo/products/prices', { product: productName, location, country });
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);

  const byDate = {};
  for (const log of (res?.data ?? []).slice().reverse()) {
    if (!log.scanned_at) continue;
    const d = log.scanned_at.slice(0, 10);
    if (new Date(d) < cutoff) continue;
    if (!byDate[d]) byDate[d] = { sum: 0, count: 0 };
    byDate[d].sum += log.price; byDate[d].count += 1;
  }

  return {
    data: Object.entries(byDate)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, { sum, count }]) => ({ date, avg_price: Math.round((sum / count) * 100) / 100 })),
  };
}

// ── Compare stores ────────────────────────────────────────────────────────────
export async function comparePrices(productName, { location = null, country = 'PH' } = {}) {
  const res = await get('/bantay-presyo/products/compare', { product: productName, location, country });
  return { data: res?.data ?? [] };
}

// ── Hot / trending products ───────────────────────────────────────────────────
//
// Three-tier strategy so the grid is NEVER empty even on a sparse DB:
//
//  Tier 1 — MOVERS     : ≥2 log entries + ≥0.5% price diff → "was/now" card
//  Tier 2 — POPULAR    : ≥1 log entry but no notable movement → price card
//  Tier 3 — COMPARE FB : no logs at all → cheapest price from compare endpoint
//
// Items with is_mover=false render a simpler "popular" card layout.
// Movers lead the list; popular items fill remaining slots up to 12.

const _hotCache  = new Map();
const HOT_TTL_MS = 3 * 60 * 1000;

// Wider seed set → more candidates → more likely to hit data even on sparse DBs
const SEED_TERMS = ['egg','rice','oil','milk','sugar','chicken','pork','onion','bread','coffee'];

export async function getHotProducts(location = null, country = 'PH', signal = null) {
  const res = await get('/bantay-presyo/hot', { country, location: location ?? '' }, signal);
  return { data: res?.data ?? [] };
}